import React, { useMemo, useState } from "react";
import axios from "axios";
import {
  InfoCircleOutlined,
  PlusOutlined,
  FilterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Card,
  Empty,
  Flex,
  Input,
  Select,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { useTranslation } from "react-i18next";
import Constants from "../Utils/Constants";

const isMinioImageKey = (value) =>
  typeof value === "string" && value.startsWith("images/");

const normalizeLegacyUrl = (url) => {
  if (!url) return null;

  if (url.startsWith("http")) {
    return url;
  }

  return `${Constants.SERVER_URL.replace(/\/$/, "")}/${url.replace(
    /^\/+/,
    ""
  )}`;
};

export default function ExerciseCatalog({ onOpenDetails, onAddQuick }) {
  const { t, i18n } = useTranslation("ExerciseCatalog");
  const token = localStorage.getItem("token");
  const dragType = "application/x-exercise";

  const headers = useMemo(
    () => ({
      Authorization: "Bearer " + token,
      "Accept-Language": i18n.language,
    }),
    [token, i18n.language]
  );

  const [q, setQ] = useState("");
  const [level, setLevel] = useState(null);
  const [category, setCategory] = useState(null);

  const [all, setAll] = useState([]);
  const [imageUrls, setImageUrls] = useState({});

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const getSignedImageUrl = async (imageKey) => {
    if (!imageKey) return null;

    if (imageKey.startsWith("http")) {
      return imageKey;
    }

    if (isMinioImageKey(imageKey)) {
      const res = await axios.get(
        `${Constants.SERVER_URL}/api/minio/presign-download`,
        {
          params: {
            bucket: "rxapa-assets",
            key: imageKey,
          },
          headers,
        }
      );

      return res.data.downloadUrl;
    }

    return normalizeLegacyUrl(imageKey);
  };

  React.useEffect(() => {
    setLoading(true);

    axios
      .get(`${Constants.SERVER_URL}/exercises`, {
        headers,
      })
      .then((res) => {
        setAll(res.data || []);
      })
      .catch((error) => {
        setErr(error);

        const raw =
          error?.response?.data?.message ||
          error?.message ||
          "fetch_exercises_failed";

        message.error(t(`Backend:${raw}`) || "Erreur lors du chargement");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [headers, t]);

  React.useEffect(() => {
    if (!all.length) {
      setImageUrls({});
      return;
    }

    let cancelled = false;

    async function loadImages() {
      const entries = await Promise.all(
        all.map(async (exercise) => {
          if (!exercise.imageUrl) {
            return [exercise.id, null];
          }

          try {
            const signedUrl = await getSignedImageUrl(exercise.imageUrl);
            return [exercise.id, signedUrl];
          } catch {
            return [exercise.id, null];
          }
        })
      );

      if (!cancelled) {
        setImageUrls(Object.fromEntries(entries));
      }
    }

    loadImages();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [all]);

  const { levelOptions, categoryOptions } = useMemo(() => {
    const levels = new Set();
    const categories = new Set();

    for (const exercise of all) {
      const currentLevel = exercise?.level ?? exercise?.fitnessLevel;

      if (currentLevel) {
        levels.add(String(currentLevel));
      }

      if (exercise?.category) {
        categories.add(String(exercise.category));
      }
    }

    return {
      levelOptions: [...levels].sort().map((value) => ({
        label: value,
        value,
      })),
      categoryOptions: [...categories].sort().map((value) => ({
        label: value,
        value,
      })),
    };
  }, [all]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();

    return all.filter((exercise) => {
      const currentLevel = exercise?.level ?? exercise?.fitnessLevel;
      const name = String(exercise?.name || "");
      const currentCategory = String(exercise?.category || "");
      const muscles = Array.isArray(exercise?.muscles)
        ? exercise.muscles.join(" ")
        : "";

      const okText =
        !term ||
        name.toLowerCase().includes(term) ||
        currentCategory.toLowerCase().includes(term) ||
        muscles.toLowerCase().includes(term) ||
        String(currentLevel || "")
          .toLowerCase()
          .includes(term);

      const okLevel = !level || String(currentLevel || "") === level;
      const okCategory = !category || currentCategory === category;

      return okText && okLevel && okCategory;
    });
  }, [all, q, level, category]);

  function onDragStartExercise(event, exercise) {
    const payload = JSON.stringify({
      id: exercise.id ?? null,
      key: exercise.key ?? null,
      name: exercise.name ?? "",
      numberOfRepetitions: exercise.numberOfRepetitions ?? 10,
    });

    try {
      event.dataTransfer.setData(dragType, payload);
    } catch {
      event.dataTransfer.setData("text/plain", payload);
    }

    event.dataTransfer.effectAllowed = "copy";
  }

  return (
    <aside
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <Card
        size="small"
        title={
          <Space>
            <FilterOutlined /> {t("label_filters") || "Filtres"}
          </Space>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={
              t("label_search_exercises") ||
              "Rechercher (nom, catégorie, muscle, niveau)"
            }
            value={q}
            onChange={(event) => setQ(event.target.value)}
          />

          <Flex gap={8} wrap="wrap">
            <Select
              allowClear
              style={{ minWidth: 140 }}
              placeholder={t("label_level") || "Niveau"}
              options={levelOptions}
              value={level}
              onChange={setLevel}
            />

            <Select
              allowClear
              style={{ minWidth: 160 }}
              placeholder={t("label_category") || "Catégorie"}
              options={categoryOptions}
              value={category}
              onChange={setCategory}
            />
          </Flex>
        </Space>
      </Card>

      <div style={{ minHeight: 160 }}>
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: 24,
            }}
          >
            <Spin
              tip={t("label_loading_catalog") || "Chargement du catalogue…"}
            />
          </div>
        ) : err ? (
          <Empty
            description={
              t("label_error_loading") ||
              "Erreur lors du chargement des exercices."
            }
          />
        ) : filtered.length === 0 ? (
          <Empty
            description={
              q
                ? t("label_no_match") ||
                  "Aucun exercice ne correspond à la recherche."
                : t("label_no_exercises") || "Aucun exercice disponible."
            }
          />
        ) : (
          <div
            style={{
              display: "grid",
              gap: 12,
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            }}
          >
            {filtered.map((exercise) => (
              <Card
                key={exercise.id}
                size="small"
                hoverable
                draggable
                onDragStart={(event) =>
                  onDragStartExercise(event, exercise)
                }
                cover={
                  imageUrls[exercise.id] ? (
                    <img
                      loading="lazy"
                      src={imageUrls[exercise.id]}
                      alt={exercise.name}
                      style={{
                        height: 120,
                        objectFit: "cover",
                      }}
                      onError={(event) => {
                        event.currentTarget.style.display = "none";
                      }}
                    />
                  ) : null
                }
                actions={[
                  <Tooltip
                    title={t("label_details") || "Détails"}
                    key="details"
                  >
                    <InfoCircleOutlined
                      onClick={() => onOpenDetails?.(exercise)}
                    />
                  </Tooltip>,

                  <Tooltip
                    title={t("label_add_to_bloc") || "Ajouter au bloc"}
                    key="add"
                  >
                    <PlusOutlined
                      onClick={() =>
                        onAddQuick
                          ? onAddQuick(exercise)
                          : onOpenDetails?.(exercise)
                      }
                    />
                  </Tooltip>,
                ]}
              >
                <Space direction="vertical" size={4} style={{ width: "100%" }}>
                  <Typography.Text strong ellipsis title={exercise.name}>
                    {exercise.name}
                  </Typography.Text>

                  <Space wrap size={[6, 4]}>
                    {(exercise?.level ?? exercise?.fitnessLevel) && (
                      <Tag>{exercise.level ?? exercise.fitnessLevel}</Tag>
                    )}

                    {exercise?.category && (
                      <Tag color="blue">{exercise.category}</Tag>
                    )}
                  </Space>
                </Space>
              </Card>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}